import { ACTIONS_COLUMN_ID } from "../constants/dashboardConstants";
import { CARD_BACKGROUND_COLORS, TEXT_COLORS } from "../constants/styles";
import { computeFleetRouteMetric } from "../config/routesModuleCatalog";
import { computeFleetStopMetric } from "../config/stopsModuleCatalog";
import { computeFleetDriverMetric } from "../config/driversModuleCatalog";
import { computeFleetUserMetric } from "../config/usersModuleCatalog";
import { computeFleetSiteMetric } from "../config/sitesModuleCatalog";
import { computeFleetScheduleMetric } from "../config/schedulesModuleCatalog";
import {
  computeFleetVehicleMetric,
  FLEET_VEHICLE_ROW_ACTIONS,
  hasLayoutPermission,
} from "../config/vehiclesModuleCatalog";

const STAT_CARD_VARIANT_STYLES = {
  darkBlue: {
    backgroundColor: CARD_BACKGROUND_COLORS.DARK_BLUE,
    titleColor: TEXT_COLORS.WHITE,
    valueColor: TEXT_COLORS.WHITE,
  },
  green: {
    backgroundColor: CARD_BACKGROUND_COLORS.GREEN,
    titleColor: TEXT_COLORS.WHITE,
    valueColor: TEXT_COLORS.WHITE,
  },
  yellow: {
    backgroundColor: CARD_BACKGROUND_COLORS.YELLOW,
    titleColor: TEXT_COLORS.BLACK,
    valueColor: TEXT_COLORS.BLACK,
  },
  red: {
    backgroundColor: CARD_BACKGROUND_COLORS.RED,
    titleColor: TEXT_COLORS.WHITE,
    valueColor: TEXT_COLORS.WHITE,
  },
};

const isSectionInView = (section, view) => !view || !section?.view || section.view === view;

export function getLayoutSection(layout, type, view = null) {
  if (!Array.isArray(layout?.sections)) return null;

  return (
    layout.sections.find((section) => section.type === type && isSectionInView(section, view)) ||
    null
  );
}

export function getLayoutSections(layout, type = null, view = null) {
  if (!Array.isArray(layout?.sections)) return [];

  return layout.sections.filter(
    (section) => (!type || section.type === type) && isSectionInView(section, view),
  );
}

export function isLayoutItemVisibleForRole(item, roleKey) {
  if (!Array.isArray(item?.visibleFor) || item.visibleFor.length === 0) return true;
  return item.visibleFor.includes(roleKey);
}

export function getVisibleLayoutWidgets(section, roleKey) {
  if (!Array.isArray(section?.widgets)) return [];
  return section.widgets.filter((widget) => isLayoutItemVisibleForRole(widget, roleKey));
}

export function getVisibleLayoutColumns(section, roleKey) {
  if (!Array.isArray(section?.columns)) return [];
  return section.columns.filter((column) => isLayoutItemVisibleForRole(column, roleKey));
}

export function buildModuleStatCards(layout, data, computeMetric) {
  const summarySection = getLayoutSection(layout, "summaryStats");
  if (!summarySection?.widgets) return null;

  return summarySection.widgets.map((widget) => {
    const styles = STAT_CARD_VARIANT_STYLES[widget.variant] || STAT_CARD_VARIANT_STYLES.darkBlue;
    return {
      title: widget.labelKey,
      value: computeMetric(widget.metricKey, data),
      ...styles,
    };
  });
}

export function buildFleetStatCards(layout, vehicleData = []) {
  return buildModuleStatCards(layout, vehicleData, computeFleetVehicleMetric);
}

export function buildFleetRouteStatCards(layout, routeData = []) {
  return buildModuleStatCards(layout, routeData, computeFleetRouteMetric);
}

export function buildFleetStopStatCards(layout, stopsData = []) {
  return buildModuleStatCards(layout, stopsData, computeFleetStopMetric);
}

export function buildFleetDriverStatCards(layout, driversData = []) {
  return buildModuleStatCards(layout, driversData, computeFleetDriverMetric);
}

export function buildFleetUserStatCards(layout, userData = []) {
  return buildModuleStatCards(layout, userData, computeFleetUserMetric);
}

export function buildFleetSiteStatCards(layout, siteData = []) {
  return buildModuleStatCards(layout, siteData, computeFleetSiteMetric);
}

export function buildFleetScheduleStatCards(layout, scheduleData = []) {
  return buildModuleStatCards(layout, scheduleData, computeFleetScheduleMetric);
}

export function buildTableHeadersFromLayout(layout, { hasPermission, t }) {
  const tableSection = getLayoutSection(layout, "dataTable");
  if (!tableSection?.columns) return null;

  const headers = tableSection.columns
    .filter((column) => hasLayoutPermission(column.permission, hasPermission))
    .map((column) => ({
      key: column.headerKey || column.fieldKey,
      value: t(column.labelKey),
    }));

  const rowActions = tableSection.rowActions || [];
  const hasRowActions = rowActions.some((action) =>
    hasLayoutPermission(action.permission, hasPermission),
  );
  const hasExpandableRows = tableSection.features?.expandableRows === true;

  if (hasRowActions || hasExpandableRows) {
    headers.push({ key: ACTIONS_COLUMN_ID, value: t("Actions") });
  }

  return headers;
}

export function buildSchoolTableHeadersFromLayout(layout, { t, hasPermission } = {}) {
  const tableSection = getLayoutSection(layout, "dataTable");
  if (!tableSection?.columns) return null;

  const headers = tableSection.columns
    .filter((column) => hasLayoutPermission(column.permission, hasPermission))
    .map((column) => ({
      key: column.headerKey || column.fieldKey,
      value: t(column.labelKey),
    }));

  headers.push({ key: "actions", value: "" });
  return headers;
}

export function buildFleetRowActionsFromLayout(
  layout,
  { hasPermission },
  actionCatalog = FLEET_VEHICLE_ROW_ACTIONS,
) {
  const tableSection = getLayoutSection(layout, "dataTable");
  if (!tableSection?.rowActions) return null;

  return tableSection.rowActions
    .filter((action) => hasLayoutPermission(action.permission, hasPermission))
    .map((action) => actionCatalog[action.actionKey])
    .filter(Boolean);
}

export function buildFilterWidgetsFromLayout(layout) {
  const filterSection = getLayoutSection(layout, "filterBar");
  if (!filterSection?.widgets) return null;

  return filterSection.widgets.filter((widget) => widget.type === "tableFilter");
}

export function getTableFeatures(layout) {
  const tableSection = getLayoutSection(layout, "dataTable");
  return tableSection?.features || {};
}

export function getActionBarWidgets(layout, hasPermission = null) {
  const actionSection = getLayoutSection(layout, "actionBar");
  const widgets = actionSection?.widgets || [];

  if (!hasPermission) {
    return widgets;
  }

  return widgets.filter((widget) => hasLayoutPermission(widget.permission, hasPermission));
}

export function getPageTitleKey(layout) {
  const titleSection = getLayoutSection(layout, "pageTitle");
  return titleSection?.props?.labelKey || null;
}
